var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getLocalParticipant } from '../base/participants/functions';
import { extractFqnFromPath } from '../dynamic-branding/functions.any';
import { FACE_BOX_EVENT_TYPE, FACE_LANDMARKS_EVENT_TYPE, SEND_IMAGE_INTERVAL_MS } from './constants';
import logger from './logger';
/**
 * Sends the face landmarks to other participants via the data channel.
 *
 * @param {any} conference - The current conference.
 * @param  {FaceLandmarks} faceLandmarks - Face landmarks to be sent.
 * @returns {void}
 */
export function sendFaceExpressionToParticipants(conference, faceLandmarks) {
    try {
        conference.sendEndpointMessage('', {
            type: FACE_LANDMARKS_EVENT_TYPE,
            faceLandmarks
        });
    }
    catch (err) {
        logger.warn('Could not broadcast the face landmarks to the other participants', err);
    }
}
/**
 * Sends the face box to all the other participants.
 *
 * @param {any} conference - The current conference.
 * @param  {FaceBox} faceBox - Face box to be sent.
 * @returns {void}
 */
export function sendFaceBoxToParticipants(conference, faceBox) {
    try {
        conference.sendEndpointMessage('', {
            type: FACE_BOX_EVENT_TYPE,
            faceBox
        });
    }
    catch (err) {
        logger.warn('Could not broadcast the face box to the other participants', err);
    }
}
/**
 * Sends the face landmarks to prosody.
 *
 * @param {any} conference - The current conference.
 * @param  {FaceLandmarks} faceLandmarks - Face landmarks to be sent.
 * @returns {void}
 */
export function sendFaceExpressionToServer(conference, faceLandmarks) {
    try {
        conference.sendFaceLandmarks(faceLandmarks);
    }
    catch (err) {
        logger.warn('Could not send the face landmarks to prosody', err);
    }
}
/**
 * Sends face landmarks to backend.
 *
 * @param  {Object} state - Redux state.
 * @returns {boolean} - True if sent, false otherwise.
 */
export function sendFaceExpressionsWebhook(state) {
    return __awaiter(this, void 0, void 0, function* () {
        const { webhookProxyUrl: url } = state['features/base/config'];
        const { conference } = state['features/base/conference'];
        const { jwt } = state['features/base/jwt'];
        const { connection } = state['features/base/connection'];
        const jid = connection === null || connection === void 0 ? void 0 : connection.getJid();
        const localParticipant = getLocalParticipant(state);
        const { faceLandmarksBuffer } = state['features/face-landmarks'];
        if (faceLandmarksBuffer.length === 0) {
            return false;
        }
        const headers = Object.assign(Object.assign({}, jwt ? { 'Authorization': `Bearer ${jwt}` } : {}), { 'Content-Type': 'application/json' });
        const reqBody = {
            meetingFqn: extractFqnFromPath(),
            sessionId: conference === null || conference === void 0 ? void 0 : conference.sessionId,
            submitted: Date.now(),
            emotions: faceLandmarksBuffer,
            participantId: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.jwtId,
            participantName: localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.name,
            participantJid: jid
        };
        if (url) {
            try {
                const res = yield fetch(`${url}/emotions`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(reqBody)
                });
                if (res.ok) {
                    return true;
                }
                logger.error('Status error:', res.status);
            }
            catch (err) {
                logger.error('Could not send request', err);
            }
        }
        return false;
    });
}
/**
 * Gets face box for a participant id.
 *
 * @param {string} id - The participant id.
 * @param {IReduxState} state - The redux state.
 * @returns {Object}
 */
function getFaceBoxForId(id, state) {
    return state['features/face-landmarks'].faceBoxes[id];
}
/**
 * Gets the video object position for a participant id.
 *
 * @param {IReduxState} state - The redux state.
 * @param {string} id - The participant id.
 * @returns {string} - CSS object-position in the shape of '{horizontalPercentage}% {verticalPercentage}%'.
 */
export function getVideoObjectPosition(state, id) {
    const faceBox = id && getFaceBoxForId(id, state);
    if (faceBox) {
        const { right, width } = faceBox;
        if (right && width) {
            return `${right - (width / 2)}% 50%`;
        }
    }
    return '50% 50%';
}
/**
 * Gets the video object position for a participant id.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {number} - Number of milliseconds for doing face detection.
 */
export function getDetectionInterval(state) {
    const { faceLandmarks } = state['features/base/config'];
    return Math.max((faceLandmarks === null || faceLandmarks === void 0 ? void 0 : faceLandmarks.captureInterval) || SEND_IMAGE_INTERVAL_MS);
}
